import { useMemo } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Guest, GuestType, PublicEvent } from "@classview/shared-types";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { AttendeesActions } from "./attendees-actions";
import { EventOrganiser } from "./event-organiser";

type AttendeesTableProps = {
  attendees: Guest[];
  event: PublicEvent;
};

export function AttendeesTable({ attendees, event }: AttendeesTableProps) {
  const columnHelper = createColumnHelper<Guest>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "name",
        header: "Attendees",
        cell: ({ row }) => {
          return (
            <EventOrganiser
              firstName={row.original.firstName ?? ""}
              lastName={row.original.lastName ?? ""}
              showHostedBy={false}
              email={row.original.email}
            />
          );
        },
      }),
      columnHelper.display({
        id: "organisation",
        header: "Organisation",
        cell: ({ row }) => {
          if (row.original.type === GuestType.Guest) {
            return null;
          }

          return (
            <div className="text-xs text-muted-foreground">
              {row.original.organisations?.map((org) => org.name).join(", ")}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "isModerator",
        header: "Moderator",
        cell: ({ row }) => {
          return (
            <div className="user-select-none pointer-events-none flex flex-col justify-center">
              <Checkbox checked={row.original.isModerator} />
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        cell: (info) => {
          return (
            <AttendeesActions
              event={event}
              attendeeEmail={info.row.original.email}
            />
          );
        },
      }),
    ],
    [columnHelper, event],
  );
  const table = useReactTable({
    data: attendees ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
